/**
 * <custom-ic-scroller> — slider suave de columnas de imagen.
 *
 * El deslizamiento es scroll nativo con inercia (sin snap: corredizo, no
 * brusco). Este componente solo agrega:
 * - clase `custom_ic--scrollable` cuando el contenido desborda (cursor grab,
 *   y así desktop se vuelve slider automáticamente al agregar más ítems)
 * - arrastre con mouse en desktop (scrollLeft), suprimiendo el clic en links
 *   cuando hubo arrastre real.
 */
const DRAG_THRESHOLD = 4;

class CustomIcScroller extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.viewport = this.querySelector('.custom_ic__viewport');
    if (!this.viewport) return;
    this.pointerActive = false;
    this.dragging = false;
    this.suppressClick = false;

    window.addEventListener('resize', () => this.refresh(), { signal });
    this.viewport.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
    this.viewport.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
    this.viewport.addEventListener('pointerup', (e) => this.onPointerEnd(e), { signal });
    this.viewport.addEventListener('pointercancel', (e) => this.onPointerEnd(e), { signal });
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    this.addEventListener(
      'click',
      (e) => {
        if (!this.suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
        this.suppressClick = false;
      },
      { capture: true, signal }
    );

    this.refresh();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  refresh() {
    const scrollable = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
    this.classList.toggle('custom_ic--scrollable', scrollable);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.viewport.scrollWidth <= this.viewport.clientWidth) return;
    this.pointerActive = true;
    this.dragging = false;
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.viewport.scrollLeft;
  }

  onPointerMove(event) {
    if (!this.pointerActive) return;
    const deltaX = event.clientX - this.dragStartX;
    if (!this.dragging && Math.abs(deltaX) > DRAG_THRESHOLD) {
      this.dragging = true;
      this.classList.add('is-dragging');
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (e) {}
    }
    if (this.dragging) {
      event.preventDefault();
      this.viewport.scrollLeft = this.dragStartScroll - deltaX;
    }
  }

  onPointerEnd() {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    if (this.dragging) {
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }
    this.dragging = false;
    this.classList.remove('is-dragging');
  }
}

if (!customElements.get('custom-ic-scroller')) {
  customElements.define('custom-ic-scroller', CustomIcScroller);
}
